import {EventSeoSchema, type EventSchemaDetails} from '@github-ui/swp-core/components/structuredData/EventSeoSchema'

import type {ResourceDetailsPage} from '../../lib/types/contentful'

type ResourceEventFields = Pick<
  ResourceDetailsPage['fields'],
  | 'eventAttendanceMode'
  | 'startDate'
  | 'endDate'
  | 'locationName'
  | 'locationStreetAddress'
  | 'locationAddressLocality'
  | 'locationAddressRegion'
  | 'locationPostalCode'
  | 'locationAddressCountry'
  | 'virtualLocationUrl'
>

type ResourceEventSeoSchemaProps = {
  fields: ResourceEventFields
  title: string
  description?: string
  imageUrl?: string
  contributors?: Array<{name: string}>
}

export function ResourceEventSeoSchema({
  fields,
  title,
  description,
  imageUrl,
  contributors,
}: ResourceEventSeoSchemaProps) {
  const eventDetails = getEventDetails(fields)

  return (
    <EventSeoSchema
      title={title}
      description={description}
      imageUrl={imageUrl}
      contributors={contributors}
      {...eventDetails}
    />
  )
}

function getEventDetails(fields: ResourceEventFields): EventSchemaDetails {
  const {
    endDate,
    eventAttendanceMode,
    locationAddressCountry,
    locationAddressLocality,
    locationAddressRegion,
    locationName,
    locationPostalCode,
    locationStreetAddress,
    startDate,
    virtualLocationUrl,
  } = fields

  const address = {
    streetAddress: locationStreetAddress || undefined,
    addressLocality: locationAddressLocality || undefined,
    addressRegion: locationAddressRegion || undefined,
    postalCode: locationPostalCode || undefined,
    addressCountry: locationAddressCountry || undefined,
  }
  const hasAddress = Object.values(address).some(Boolean)
  const physicalLocation =
    locationName || hasAddress
      ? {type: 'physical' as const, name: locationName || undefined, address: hasAddress ? address : undefined}
      : undefined
  const virtualLocation = virtualLocationUrl ? {type: 'virtual' as const, url: virtualLocationUrl} : undefined
  const locations = [
    eventAttendanceMode !== 'online' ? physicalLocation : undefined,
    eventAttendanceMode !== 'in-person' ? virtualLocation : undefined,
  ].filter(location => location !== undefined)

  return {
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    attendanceMode: eventAttendanceMode,
    location: locations.length > 1 ? locations : locations[0],
  }
}
